import React, {useState, useEffect} from 'react'
import Diagnosis from './tab-components/Diagnosis'
import PatientDetails from './tab-components/PatientDetails'
import Documents from './tab-components/Documents'
import DocumentsFGL from './tab-components/DocumentsFGL'
import Billing from './Billing'
import {
  FaExclamationCircle,
  FaUserCircle,
  FaSearch,
  FaFileInvoiceDollar,
  FaCalendarAlt,
} from 'react-icons/fa'
import {FaFile} from 'react-icons/fa6'
import CalendarPage from './tab-components/Calendar'
import DocumentsDailyMonitoring from './tab-components/DocumentsDailyMonitoring'

interface InpatientPageProps {
  claimDetails: any
  onDocumentsStatusChange: (status: boolean) => void
  isEditingDiagnosis: boolean
  onEditClick: () => void
  onDocumentClick: (docLabel: string) => void
  onValidateDiagnosis: (diagnosisData: any) => void
  isFromDailyMonitoring?: boolean
  onDocumentRealtimeUpdate?: (data: any) => void
  onDiagnosisDataChange: (data: any) => void
  isRequestFGL?: boolean
  currentView?: 'inpatient' | 'calendar' | 'dailyMonitoringDetail' | 'discharged'
  setIsReupload: (isReupload: boolean) => void
}

const InpatientPage: React.FC<InpatientPageProps> = ({
  claimDetails,
  onDocumentsStatusChange,
  isEditingDiagnosis,
  onEditClick,
  onDocumentClick,
  onValidateDiagnosis,
  isFromDailyMonitoring = false,
  onDocumentRealtimeUpdate,
  onDiagnosisDataChange,
  isRequestFGL = false,
  currentView = 'inpatient',
  setIsReupload,
}) => {
  const claimStatus = claimDetails?.claimDetails?.status
  const isFGLRequest =
    (claimStatus === 'Request FGL' ||
      claimStatus === 'FGL RELEASED' ||
      claimStatus === 'FGL Released' ||
      claimStatus === 'Final Document Complete' ||
      claimStatus === 'FGL Review' ||
      claimStatus === 'Pending on FGL') &&
    currentView === 'discharged'

  // Set default tab based on claim status
  // const defaultTab =
  //   claimStatus === 'Pending on FGL' ||
  //   claimStatus === 'FGL RELEASED' ||
  //   (claimStatus === 'Request FGL' && currentView === 'discharged') ||
  //   claimStatus === 'Final Document Complete' ||
  //   claimStatus === 'FGL Review'
  //     ? 'Documents FGL'
  //     : 'Diagnosis'
  // const [activeTab, setActiveTab] = useState(defaultTab)

  const [activeTab, setActiveTab] = useState('')

  const [diagnosisData, setDiagnosisData] = useState({
    mainDiagnosis: '',
    supportingDiagnosis: '',
    supportingDiagnosis3: '',
    doctor: '',
    polyName: '',
    polyCode: '',
    remark: '',
    fax: '',
    deposit: '',
    originalIcdCodes: {
      icd1: '',
      icd2: '',
      icd3: '',
    },
    indate: claimDetails?.claimDetails?.indate || '',
  })

  useEffect(() => {
    if (currentView === 'inpatient') {
      setActiveTab('Diagnosis')
    } else if (
      currentView === 'discharged' &&
      (claimStatus === 'Request FGL' ||
        claimStatus === 'FGL RELEASED' ||
        claimStatus === 'FGL Released' ||
        claimStatus === 'Pending on FGL' ||
        claimStatus === 'Final Document Complete' ||
        claimStatus === 'FGL Review')
    ) {
      setActiveTab('Documents FGL')
    } else if (currentView === 'calendar') {
      setActiveTab('Calendar')
    }
  }, [currentView, claimStatus]) // Track both actual values

  const handleDiagnosisDataChange = (newData: any) => {
    setDiagnosisData((prev) => ({
      ...prev,
      ...newData,
    }))
    onDiagnosisDataChange(newData)
  }

  const documentsTabLabel = isFGLRequest ? 'Documents FGL' : 'Documents'

  const renderTabContent = () => {
    const claimStatus = claimDetails?.claimDetails?.status
    const isFGLRequest =
      claimStatus === 'Request FGL' ||
      claimStatus === 'FGL RELEASED' ||
      claimStatus === 'FGL Released' ||
      claimStatus === 'Final Document Complete' ||
      claimStatus === 'Pending on FGL'

    switch (activeTab) {
      case 'Diagnosis':
        return (
          <Diagnosis
            claimDetails={claimDetails}
            isEditing={isEditingDiagnosis}
            onEditClick={onEditClick}
            onValidateDiagnosis={onValidateDiagnosis}
            isFromDailyMonitoring={isFromDailyMonitoring}
            diagnosisData={diagnosisData}
            onDiagnosisDataChange={handleDiagnosisDataChange}
          />
        )
      case 'Patient Details':
        return <PatientDetails claimDetails={claimDetails} />
      case 'Documents':
        return !isFGLRequest ? (
          <Documents
            claimDetails={claimDetails}
            onDocumentClick={onDocumentClick}
            onDocumentsStatusChange={onDocumentsStatusChange}
            onDocumentRealtimeUpdate={onDocumentRealtimeUpdate}
          />
        ) : (
          <Documents
            claimDetails={claimDetails}
            onDocumentClick={onDocumentClick}
            onDocumentsStatusChange={onDocumentsStatusChange}
            onDocumentRealtimeUpdate={onDocumentRealtimeUpdate}
          />
        )
      case 'Documents FGL':
        return isFGLRequest ? (
          <DocumentsFGL
            claimDetails={claimDetails}
            onDocumentClick={onDocumentClick}
            onDocumentsStatusChange={onDocumentsStatusChange}
            onDocumentRealtimeUpdate={onDocumentRealtimeUpdate}
            setIsReupload={setIsReupload}
          />
        ) : (
          <DocumentsFGL
            claimDetails={claimDetails}
            onDocumentClick={onDocumentClick}
            onDocumentsStatusChange={onDocumentsStatusChange}
            onDocumentRealtimeUpdate={onDocumentRealtimeUpdate}
            setIsReupload={setIsReupload}
          />
        )
      case 'Billing':
        return <Billing claimDetails={claimDetails} />
      case 'Calendar':
        return <CalendarPage claimDetails={claimDetails} />
      case 'Documents Daily Monitoring':
        return (
          <DocumentsDailyMonitoring
            claimDetails={claimDetails}
            onDocumentClick={onDocumentClick}
            onDocumentsStatusChange={onDocumentsStatusChange}
            onDocumentRealtimeUpdate={onDocumentRealtimeUpdate}
          />
        )
      default:
        return null
    }
  }

  const tabStyle = {
    cursor: 'pointer',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0',
    backgroundColor: 'transparent',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    border: '1px solid transparent',
    marginRight: '8px',
  }

  const activeTabStyle = {
    backgroundColor: '#EBF7F9',
    color: '#059BB6',
    border: '1px solid #E2EDF3',
    boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
  }

  const tabIconStyle = {
    width: '20px',
    height: '20px',
    marginRight: '8px',
    transition: 'all 0.2s ease',
  }

  const activeIconStyle = {
    color: '#059BB6',
  }

  const tabTextStyle = {
    fontSize: '0.95rem',
    fontWeight: 500,
    transition: 'all 0.2s ease',
  }

  const tabsContainerStyle = {
    display: 'flex',
    padding: '8px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    marginBottom: '24px',
  }

  const contentContainerStyle = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    padding: '24px',
    minHeight: '500px',
  }

  const getTabStyle = (isActive: boolean) => ({
    ...tabStyle,
    ...(isActive ? activeTabStyle : {}),
    ':hover': {
      backgroundColor: isActive ? '#EBF7F9' : '#F8FAFC',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
    },
  })

  return (
    <>
      <div className='frame-main' style={{padding: '24px'}}>
        <div className='frame-tabs' style={tabsContainerStyle}>
          {currentView === 'calendar' ? (
            <>
              <div
                className={`frame-tab3 ${activeTab === 'Calendar' ? 'active' : ''}`}
                onClick={() => setActiveTab('Calendar')}
                style={getTabStyle(activeTab === 'Calendar')}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <FaCalendarAlt
                    style={{
                      ...tabIconStyle,
                      ...(activeTab === 'Calendar' ? activeIconStyle : {}),
                    }}
                  />
                  <span
                    style={{
                      ...tabTextStyle,
                      color: activeTab === 'Calendar' ? '#059BB6' : '#64748B',
                    }}
                  >
                    Calendar
                  </span>
                </div>
              </div>

              <div
                className={`frame-tab3 ${
                  activeTab === 'Documents Daily Monitoring' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('Documents Daily Monitoring')}
                style={getTabStyle(activeTab === 'Documents Daily Monitoring')}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <FaFile
                    style={{
                      ...tabIconStyle,
                      ...(activeTab === 'Documents Daily Monitoring' ? activeIconStyle : {}),
                    }}
                  />
                  <span
                    style={{
                      ...tabTextStyle,
                      color: activeTab === 'Documents Daily Monitoring' ? '#059BB6' : '#64748B',
                    }}
                  >
                    Documents
                  </span>
                </div>
              </div>
            </>
          ) : !isFGLRequest ? (
            <>
              <div
                className={`frame-tab1 ${activeTab === 'Diagnosis' ? 'active' : ''}`}
                onClick={() => setActiveTab('Diagnosis')}
                style={getTabStyle(activeTab === 'Diagnosis')}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <FaSearch
                    style={{
                      ...tabIconStyle,
                      ...(activeTab === 'Diagnosis' ? activeIconStyle : {}),
                    }}
                  />
                  <span
                    style={{
                      ...tabTextStyle,
                      color: activeTab === 'Diagnosis' ? '#059BB6' : '#64748B',
                    }}
                  >
                    Diagnosis
                  </span>
                </div>
              </div>

              <div
                className={`frame-tab2 ${activeTab === 'Patient Details' ? 'active' : ''}`}
                onClick={() => setActiveTab('Patient Details')}
                style={getTabStyle(activeTab === 'Patient Details')}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <FaUserCircle
                    style={{
                      ...tabIconStyle,
                      ...(activeTab === 'Patient Details' ? activeIconStyle : {}),
                    }}
                  />
                  <span
                    style={{
                      ...tabTextStyle,
                      color: activeTab === 'Patient Details' ? '#059BB6' : '#64748B',
                    }}
                  >
                    Patient Details
                  </span>
                </div>
              </div>

              <div
                className={`frame-tab3 ${activeTab === 'Documents' ? 'active' : ''}`}
                onClick={() => setActiveTab('Documents')}
                style={getTabStyle(activeTab === 'Documents')}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <FaFile
                    style={{
                      ...tabIconStyle,
                      ...(activeTab === 'Documents' ? activeIconStyle : {}),
                    }}
                  />
                  <span
                    style={{
                      ...tabTextStyle,
                      color: activeTab === 'Documents' ? '#059BB6' : '#64748B',
                    }}
                  >
                    Documents
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                className={`frame-tab3 ${activeTab === 'Documents FGL' ? 'active' : ''}`}
                onClick={() => setActiveTab('Documents FGL')}
                style={getTabStyle(activeTab === 'Documents FGL')}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <FaFile
                    style={{
                      ...tabIconStyle,
                      ...(activeTab === 'Documents FGL' ? activeIconStyle : {}),
                    }}
                  />
                  <span
                    style={{
                      ...tabTextStyle,
                      color: activeTab === 'Documents FGL' ? '#059BB6' : '#64748B',
                    }}
                  >
                    Documents FGL
                  </span>
                </div>
              </div>

              <div
                className={`frame-tab3 ${activeTab === 'Billing' ? 'active' : ''}`}
                onClick={() => setActiveTab('Billing')}
                style={getTabStyle(activeTab === 'Billing')}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <FaFileInvoiceDollar
                    style={{
                      ...tabIconStyle,
                      ...(activeTab === 'Billing' ? activeIconStyle : {}),
                    }}
                  />
                  <span
                    style={{
                      ...tabTextStyle,
                      color: activeTab === 'Billing' ? '#059BB6' : '#64748B',
                    }}
                  >
                    Billing
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
        <div className='frame-content' style={contentContainerStyle}>
          {renderTabContent()}
        </div>
      </div>

      <style>
        {`
          .frame-tab1:hover, .frame-tab2:hover, .frame-tab3:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 6px rgba(0,0,0,0.05);
          }

          .frame-tab1, .frame-tab2, .frame-tab3 {
            transition: all 0.2s ease-in-out;
          }

          .frame-content {
            animation: fadeIn 0.3s ease-in-out;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </>
  )
}

export default InpatientPage
